(() => {
  'use strict';

  const config = document.querySelector('script[data-aeris-analytics]');
  if (!config || window.__aerisAnalyticsV1 || window.Shopify?.designMode === true) return;
  window.__aerisAnalyticsV1 = true;

  const measurementId = config.dataset.measurementId;
  if (measurementId !== 'G-TXJ4QQPCK0') return;
  const debug = window.Shopify?.theme?.role === 'unpublished'
    && new URLSearchParams(window.location.search).get('aeris_analytics_debug') === '1';
  const clickContexts = new WeakMap();
  const pendingOpenings = new WeakMap();
  const debugEntries = [];
  let privacyLoadAttempted = false;
  const topics = new Set(['compatibility', 'material', 'assembly', 'dispatch', 'returns', 'other_colors', 'description', 'assembly_guide', 'shipping_returns']);

  const consentState = () => {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') return 'unavailable';
    try {
      return window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true ? 'allowed' : 'denied';
    } catch {
      return 'error';
    }
  };

  const analyticsAllowed = () => consentState() === 'allowed';

  const debugLog = (status, name, parameters) => {
    if (!debug) return;
    const entry = {
      status,
      consent: consentState(),
      consent_api: typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function' ? 'available' : 'unavailable',
      native_tag: typeof window.gtag === 'function' ? 'available' : 'unavailable',
      ...(name ? { event: name, parameters } : {})
    };
    debugEntries.push(entry);
    if (debugEntries.length > 20) debugEntries.shift();
    config.dataset.analyticsDebug = JSON.stringify(debugEntries);
    window.console?.info?.('[AERIS Analytics]', entry);
  };

  const loadPrivacyApi = () => {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') return;
    if (privacyLoadAttempted) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      debugLog('privacy_loader_unavailable');
      return;
    }
    privacyLoadAttempted = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        debugLog(error ? 'privacy_api_load_error' : 'privacy_api_loaded');
      });
    } catch {
      debugLog('privacy_api_load_error');
    }
  };

  const send = (name, parameters, allowedAtAction = true) => {
    if (!allowedAtAction || !analyticsAllowed()) {
      debugLog('dropped_no_consent', name, parameters);
      return;
    }
    if (typeof window.gtag !== 'function') {
      debugLog('dropped_no_native_tag', name, parameters);
      return;
    }
    try {
      window.gtag('event', name, {
        ...parameters,
        send_to: measurementId,
        ...(debug ? { debug_mode: true } : {})
      });
      debugLog('sent_to_native_tag', name, parameters);
    } catch {
      debugLog('native_tag_error', name, parameters);
    }
  };

  const colorValue = value => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'noir' || normalized === 'black') return 'black';
    if (normalized === 'blanc' || normalized === 'white') return 'white';
    return undefined;
  };

  const selection = (scope, viewer) => {
    const color = colorValue(viewer?.currentOptions?.[0])
      || colorValue(scope?.querySelector('[data-color-option="true"] input:checked')?.value)
      || colorValue(scope?.dataset.selectedColor);
    const quantity = scope?.querySelector('[data-option-index="1"] input:checked')?.value;
    const configuration = quantity === 'single' || quantity === 'pair' ? quantity : undefined;
    return { ...(color ? { color } : {}), ...(configuration ? { configuration } : {}) };
  };

  const viewerLocation = button => button.matches('[data-ar-capsule-open]')
    ? 'product_gallery'
    : button.closest('aeris-product') ? 'product_viewer' : 'home_offer';

  document.addEventListener('click', event => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    const allowed = analyticsAllowed();
    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    if (details?.matches('details[data-aeris-topic]') && !details.open) {
      pendingOpenings.set(details, { allowed, at: performance.now() });
    }
    const button = event.target.closest('[data-offer-color], [data-ar-open], [data-ar-capsule-open], [data-shopify-xr]');
    if (!button || button.disabled) return;
    const scope = button.closest('aeris-product, aeris-offer');
    const viewer = button.closest('aeris-ar-viewer') || scope?.querySelector('aeris-ar-viewer');
    clickContexts.set(event, {
      allowed,
      button,
      scope,
      viewer,
      wasOpen: viewer?.querySelector('dialog')?.open === true,
      previousColor: scope?.querySelector('[data-offer-color][aria-pressed="true"]')?.dataset.offerColor
    });
  }, true);

  document.addEventListener('toggle', event => {
    const details = event.target;
    if (!(details instanceof Element) || !details.matches('details[data-aeris-topic]')) return;
    const opening = pendingOpenings.get(details);
    pendingOpenings.delete(details);
    if (!opening || !details.open || performance.now() - opening.at > 1000) return;
    const topic = details.dataset.aerisTopic;
    const location = details.dataset.aerisLocation;
    if (!topics.has(topic) || !['home_faq', 'product_details'].includes(location)) return;
    send('aeris_faq_open', { content_topic: topic, interaction_location: location }, opening.allowed);
  }, true);

  document.addEventListener('change', event => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    const input = event.target;
    if (!input.matches('input[type="radio"]') || !input.checked) return;
    const scope = input.closest('aeris-product');
    const field = input.closest('[data-option-index]');
    if (!scope || !field) return;
    const option = field.dataset.colorOption === 'true' ? 'color'
      : field.dataset.optionIndex === '1' ? 'configuration' : undefined;
    const values = selection(scope);
    if (!option || !values.color || !values.configuration) return;
    send('aeris_configuration_change', {
      ...values,
      option_changed: option,
      interaction_location: 'product_options'
    });
  });

  document.addEventListener('click', event => {
    const context = clickContexts.get(event);
    if (!context) return;
    const { allowed, button, scope, viewer, wasOpen, previousColor } = context;
    if (button.matches('[data-offer-color]')) {
      const color = colorValue(button.dataset.offerColor);
      if (!color || color === previousColor || scope?.dataset.selectedColor !== color) return;
      send('aeris_configuration_change', {
        color,
        option_changed: 'color',
        interaction_location: 'home_offer'
      }, allowed);
      return;
    }
    if (button.matches('[data-ar-open], [data-ar-capsule-open]')) {
      if (wasOpen || viewer?.querySelector('dialog')?.open !== true) return;
      send('aeris_3d_open', {
        ...selection(scope, viewer),
        interaction_location: viewerLocation(button)
      }, allowed);
      return;
    }
    if (button.matches('[data-shopify-xr]') && viewer?.querySelector('dialog')?.open === true) {
      send('aeris_ar_request', {
        ...selection(scope, viewer),
        interaction_location: viewerLocation(button)
      }, allowed);
    }
  });

  document.addEventListener('visitorConsentCollected', () => debugLog('consent_changed'));
  window.addEventListener('load', () => {
    loadPrivacyApi();
    debugLog('page_loaded');
  }, { once: true });
  debugLog('initialized');
  loadPrivacyApi();
})();
